import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { Pressable } from "react-native";
import { Box, HStack, Text } from "native-base";
import { isWeb } from "@madmedical/utils";
import Icon from "../icons/Icon";
import { colors, timeGridLinesHorizontalBottom, timeGridLinesVerticalSolid, } from "./useStyles";
export default function Evaluations({ graphData = {}, timeData, evaluationBgHighlight, setEvaluationBgHighlight, setCommentState, setTooltip, }) {
    var _a;
    const { coords: graphCoords = [], evaluations } = graphData;
    const { timeCoords } = timeData;
    // ###################################s
    const validEvaluations = useMemo(() => {
        const evaluationPositions = evaluations === null || evaluations === void 0 ? void 0 : evaluations.reduce((positions, { xStart, xEnd, name }, i) => {
            var _a, _b, _c, _d, _e;
            const isValid = !!(graphCoords === null || graphCoords === void 0 ? void 0 : graphCoords.length) &&
                xStart &&
                xStart >= ((_a = graphCoords[0]) === null || _a === void 0 ? void 0 : _a.x) &&
                xStart <= ((_b = graphCoords[graphCoords.length - 1]) === null || _b === void 0 ? void 0 : _b.x) &&
                xEnd &&
                xEnd >= ((_c = graphCoords[0]) === null || _c === void 0 ? void 0 : _c.x) &&
                xEnd <= ((_d = graphCoords[graphCoords.length - 1]) === null || _d === void 0 ? void 0 : _d.x)
                ? true
                : false;
            if (!isValid) {
                console.log(`Outside time-window: evaluation #${i}`);
                return;
            }
            const round = (i) => Math.round(i * 100) / 100;
            const xStartRounded = round(xStart);
            const xEndRounded = round(xEnd);
            const gridLineStart = Math.max(0, timeCoords.findIndex(({ x }) => round(x) > xStartRounded - 1));
            const gridLineEnd = Math.min(timeCoords.length - 1, timeCoords.findIndex(({ x }) => round(x) >= xEndRounded));
            if (i === 0 &&
                positions &&
                ((_e = timeCoords[gridLineEnd]) === null || _e === void 0 ? void 0 : _e.x) !== undefined) {
                positions.push({
                    row: 0,
                    gridLineStart,
                    gridLineEnd,
                    xStart: timeCoords[gridLineStart].x,
                    xEnd: timeCoords[gridLineEnd].x,
                    name,
                });
                return positions;
            }
            if (i > 0 && positions) {
                const reservedRows = [];
                positions.forEach((evPos) => {
                    if ((gridLineStart >= evPos.gridLineStart &&
                        gridLineStart < evPos.gridLineEnd) ||
                        (evPos.gridLineStart >= gridLineStart &&
                            evPos.gridLineStart < gridLineEnd))
                        reservedRows.push(evPos.row);
                });
                const reservedRowsUnique = [...new Set(reservedRows)];
                const rowIndexWithFreeSlot = reservedRowsUnique
                    .sort((a, b) => a - b)
                    .findIndex((row, i) => row !== i);
                const row = rowIndexWithFreeSlot === -1
                    ? reservedRowsUnique[reservedRowsUnique.length - 1] + 1 || 0
                    : rowIndexWithFreeSlot;
                positions.push({
                    row,
                    gridLineStart,
                    gridLineEnd,
                    xStart: timeCoords[gridLineStart].x,
                    xEnd: timeCoords[gridLineEnd].x,
                    name,
                });
                return positions;
            }
            return positions;
        }, []);
        const rowsMapCallback = ({ row, }) => row;
        const totalRows = evaluationPositions
            ? 1 + Math.max(0, ...evaluationPositions.map(rowsMapCallback))
            : 0;
        return {
            positions: evaluationPositions,
            totalRows: isFinite(totalRows) ? totalRows : 1,
        };
    }, [evaluations, graphCoords, timeCoords]);
    // ###################################
    useEffect(() => {
        if (!((evaluations === null || evaluations === void 0 ? void 0 : evaluations.length) && (validEvaluations === null || validEvaluations === void 0 ? void 0 : validEvaluations.positions)))
            return;
        const index = evaluations.findIndex((evaluation) => evaluation.isSelected);
        if (index === -1)
            return;
        setEvaluationBgHighlight((state) => {
            var _a, _b, _c, _d;
            return (Object.assign(Object.assign({}, state), { start: (_b = (_a = validEvaluations.positions) === null || _a === void 0 ? void 0 : _a[index].gridLineStart) !== null && _b !== void 0 ? _b : null, end: (_d = (_c = validEvaluations.positions) === null || _c === void 0 ? void 0 : _c[index].gridLineEnd) !== null && _d !== void 0 ? _d : null, selected: index }));
        });
    }, [evaluations, setEvaluationBgHighlight, validEvaluations.positions]);
    // ###################################
    const evaluationBoxInner = {
        borderRadius: 24,
        borderWidth: 1,
        paddingLeft: 6,
        paddingRight: 6,
    };
    const evaluationBoxMiddle = {
        backgroundColor: colors.white,
        paddingBottom: 4,
        paddingTop: 4,
        borderRadius: 24,
    };
    const evaluationBoxOuter = {
        paddingLeft: 5,
        paddingRight: 3,
        position: "absolute",
        overflow: "hidden",
    };
    const evaluationText = {
        borderRadius: isWeb ? 24 : 0,
        color: colors.grey800,
        fontSize: 12,
        lineHeight: 18,
        overflow: "hidden",
        overflowWrap: "normal",
        whiteSpace: "nowrap",
    };
    const fontWeight = (i) => ({
        fontWeight: evaluationBgHighlight.selected === i ? "700" : "400",
    });
    // ###################################
    return ((_a = validEvaluations.positions) === null || _a === void 0 ? void 0 : _a.length) ? (_jsxs(Box, Object.assign({ position: "relative", style: timeGridLinesHorizontalBottom, height: `${8 + 24 * validEvaluations.totalRows}px` }, { children: [timeCoords.map(({ x }, i) => (_jsx(Box, { style: timeGridLinesVerticalSolid, left: `${x}px` }, `evaluation-gridlines-${i}`))), evaluations === null || evaluations === void 0 ? void 0 : evaluations.map(({ id, name, onPress }, i) => {
                var _a;
                return ((_a = validEvaluations.positions) === null || _a === void 0 ? void 0 : _a[i]) ? (_jsx(Box, Object.assign({ style: evaluationBoxOuter, left: `${validEvaluations.positions[i].xStart}px`, top: `${24 * validEvaluations.positions[i].row}px`, width: `${validEvaluations.positions[i].xEnd -
                        validEvaluations.positions[i].xStart}px` }, { children: _jsx(Pressable, Object.assign({ onPress: () => {
                            onPress();
                            setEvaluationBgHighlight((state) => {
                                var _a, _b, _c, _d;
                                return state.selected === i
                                    ? {
                                        start: null,
                                        end: null,
                                        selected: -1,
                                    }
                                    : {
                                        start: (_b = (_a = validEvaluations.positions) === null || _a === void 0 ? void 0 : _a[i].gridLineStart) !== null && _b !== void 0 ? _b : null,
                                        end: (_d = (_c = validEvaluations.positions) === null || _c === void 0 ? void 0 : _c[i].gridLineEnd) !== null && _d !== void 0 ? _d : null,
                                        selected: i,
                                    };
                            });
                            setCommentState((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
                            setTooltip((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
                        } }, { children: _jsx(Box, Object.assign({ style: evaluationBoxMiddle }, { children: _jsxs(HStack, Object.assign({ style: evaluationBoxInner, alignItems: "center", backgroundColor: evaluationBgHighlight.selected === i
                                    ? colors.green50
                                    : colors.grey50, borderColor: evaluationBgHighlight.selected === i
                                    ? colors.green300
                                    : colors.grey150 }, { children: [_jsx(Box, Object.assign({ mr: 1 }, { children: _jsx(Icon, { name: "circleSolid", fill: evaluationBgHighlight.selected ===
                                                i
                                                ? colors.green300
                                                : colors.grey150, size: 6 }) })), _jsx(Text, Object.assign({ numberOfLines: 1, nativeID: id, 
                                        // does not accept fontWeight neither as attribute nor as inline style
                                        style: [evaluationText, fontWeight(i)] }, { children: name }))] })) })) })) }), id)) : (_jsx(_Fragment, {}));
            })] }))) : (_jsx(_Fragment, {}));
}
